/**
 * Re-run the fetch for an `<img>` that already looks failed, so its outcome
 * arrives as a fresh event on an element that is listening.
 *
 * **The problem is ordering, not judgement.** Atlas server-renders, so the
 * browser starts an `<img>`'s request while parsing the HTML and can finish
 * failing it *before* React hydrates and attaches `onError`. The `error` event
 * does not replay, so the verdict is simply lost: the fallback arm every image
 * slot has is correctly wired and never runs, and Chromium's broken-image glyph
 * stays on screen for the life of the page (#539). The donor has no such window
 * — modsy is a client-only Vite SPA, so over there a handler is always attached
 * before the request starts — which makes this a restoration of the source's
 * behaviour rather than a change to it.
 *
 * **Why this re-arms rather than judging.** The obvious fix is to read the
 * element at mount and declare it failed:
 *
 * ```ts
 * if (img.complete && img.naturalWidth === 0) noteFailed();
 * ```
 *
 * It was written during #496, it did fix the cold load, and it was reverted,
 * for two separate reasons:
 *
 * 1. A **freshly created** `<img>` reports exactly that state — `complete` is
 *    true while both `src` and `srcset` are absent, and `naturalWidth` is 0
 *    before any fetch — so the same reading that means "this failed" also means
 *    "this has not started", and nothing at the call site can tell them apart.
 * 2. Even on a settled element the reading can describe the **previous**
 *    attempt rather than the current one, so it re-asserts a stale verdict and
 *    breaks the retry `useItemImageFailure` exists to allow: pointing a slot
 *    back at bytes that failed earlier must try again (A -> B -> A), and so
 *    must pointing it at a new rendition ladder for the same row.
 *    `item-image.browser.test.tsx`'s four retry specs are that requirement, and
 *    they are what failed on the revert.
 *
 * Re-arming sidesteps both. It states no verdict and stores nothing: it asks
 * the browser to fetch again, and whatever the answer is arrives as a normal
 * `load` or `error` on a handler that is now attached. A fresh element that has
 * not started simply starts — the same request it was going to make — and a
 * healthy one is left alone, because a decoded image has a non-zero
 * `naturalWidth`.
 *
 * **`removeAttribute` + `setAttribute`, never `el.src = el.src`.** The IDL
 * setter reads back the *resolved absolute* URL, so assigning it rewrites a
 * relative attribute to an absolute one and the DOM stops matching what React
 * rendered — a hydration-shaped bug traded for a hydration bug.
 *
 * **Pass this function itself as the `ref`, never an arrow that calls it.** A
 * ref callback re-runs whenever its identity changes, so an inline closure
 * would re-arm on every render — and a slot whose picture genuinely fails would
 * re-fetch forever. As a module-level function it is stable, so React calls it
 * once per mounted element, which is the whole intent. It tolerates the `null`
 * React passes on detach.
 */
export function rearmImageIfAlreadyFailed(el: HTMLImageElement | null): void {
  if (!el || !el.complete || el.naturalWidth !== 0) return;

  const src = el.getAttribute("src");
  if (src === null) return;

  el.removeAttribute("src");
  el.setAttribute("src", src);
}
